import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Upgrade() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [perks, setPerks] = useState([]);
  const [selected, setSelected] = useState("mirro_yearly");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/payments/plans").then((r) => {
      setPlans(r.data.plans); setPerks(r.data.perks);
      const f = r.data.plans.find((p) => p.founding);
      setSelected(f ? f.lookup_key : (r.data.plans.find((p) => p.lookup_key === "mirro_yearly") || r.data.plans[0])?.lookup_key);
    }).catch(() => {});
  }, []);

  const isPremium = user?.plan === "premium";

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: selected, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[620px] h-[620px] rounded-full opacity-30"
        style={{ background: "radial-gradient(circle, hsl(195 100% 55% / 0.5) 0%, transparent 62%)", filter: "blur(70px)" }} />
      <div className="pointer-events-none absolute bottom-0 -right-24 w-[420px] h-[420px] rounded-full opacity-25"
        style={{ background: "radial-gradient(circle, hsl(12 90% 62%) 0%, transparent 60%)", filter: "blur(80px)" }} />
      <Navbar />

      <main className="relative max-w-3xl mx-auto px-5 sm:px-8 pt-8 pb-24">
        <button onClick={() => navigate(-1)} data-testid="upgrade-back" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Xcelio Premium
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-3">Become fully yourself.</h1>
          <p className="text-muted-foreground max-w-md mx-auto">Unlock Xcelio's real voice, the live avatar, and unlimited practice.</p>
        </motion.div>

        {isPremium ? (
          <div className="glass rounded-3xl p-8 text-center max-w-md mx-auto" data-testid="upgrade-already-premium">
            <ShieldCheck className="w-10 h-10 text-primary mx-auto mb-4" />
            <h2 className="font-serif text-2xl mb-2">You're on Premium</h2>
            <p className="text-muted-foreground text-sm mb-6">You have full access to everything Xcelio offers.</p>
            <Button className="rounded-full" onClick={() => navigate("/app")} data-testid="upgrade-go-home">Back to Xcelio</Button>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-4 mb-8" data-testid="upgrade-plans">
              {[...plans].sort((a, b) => (b.founding ? 1 : 0) - (a.founding ? 1 : 0)).map((p) => {
                const active = selected === p.lookup_key;
                return (
                  <button key={p.lookup_key} onClick={() => setSelected(p.lookup_key)} data-testid={`plan-${p.lookup_key}`}
                    className={`relative text-left rounded-3xl p-6 transition-all duration-300 border-2 ${p.founding ? "sm:col-span-2 " : ""}${active ? "border-primary bg-card shadow-xl -translate-y-0.5" : "border-border bg-card/60 hover:border-primary/40"}`}>
                    {p.badge && <span className="absolute -top-3 right-5 text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full bg-gradient-to-r from-teal-400 to-blue-500 text-white shadow">{p.badge}</span>}
                    <div className="text-sm text-muted-foreground mb-1">{p.name}</div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-serif text-4xl tracking-tight">{p.price}</span>
                      <span className="text-sm text-muted-foreground">/{p.interval}</span>
                    </div>
                    {p.save && <span className="inline-block mt-2 text-xs font-medium text-teal-600">{p.save}</span>}
                    <p className="text-sm text-muted-foreground mt-3">{p.blurb}</p>
                    {p.founding && p.founding_cap > 0 && p.spots_left !== null && p.spots_left !== undefined && (
                      <div className="mt-3" data-testid="founding-countdown">
                        <div className="flex items-baseline justify-between text-xs">
                          <span className="text-amber-300 font-medium" data-testid="founding-countdown-left">{p.spots_left} of {p.founding_cap} Founding Light places left</span>
                          <span className="text-muted-foreground">{p.claimed || 0} claimed</span>
                        </div>
                        <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-amber-300 to-amber-500 transition-[width] duration-700" style={{ width: `${Math.max(4, Math.min(100, ((p.claimed || 0) / p.founding_cap) * 100))}%` }} />
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1.5">Once they're gone, Founding Light closes for good.</p>
                      </div>
                    )}
                    {p.founding && !(p.founding_cap > 0) && (p.spots_left !== null || p.ends_at) && (
                      <p className="text-xs text-amber-400 mt-2" data-testid="founding-spots">
                        {p.spots_left !== null && p.spots_left !== undefined ? `Only ${p.spots_left} founding spots left` : ""}{p.spots_left !== null && p.spots_left !== undefined && p.ends_at ? " · " : ""}{p.ends_at ? `Ends ${new Date(p.ends_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}
                      </p>
                    )}
                    <span className={`mt-4 flex items-center justify-center w-6 h-6 rounded-full border-2 ${active ? "border-primary bg-primary text-white" : "border-border"}`}>
                      {active && <Check className="w-3.5 h-3.5" />}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="glass rounded-3xl p-7 mb-8">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Everything in Premium</p>
              <ul className="space-y-3">
                {perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-3 text-sm">
                    <span className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-teal-400/20 text-teal-600 shrink-0"><Check className="w-3 h-3" /></span>
                    {perk}
                  </li>
                ))}
              </ul>
            </div>

            <Button size="lg" onClick={checkout} disabled={loading} data-testid="upgrade-checkout-btn"
              className="w-full rounded-full text-base py-6 bg-gradient-to-r from-blue-500 to-teal-400 hover:opacity-95 shadow-lg shadow-blue-500/20">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Upgrade to Premium — {plans.find((p) => p.lookup_key === selected)?.price}</>}
            </Button>
            <p className="text-center text-xs text-muted-foreground mt-4">Secure checkout by Stripe · Cancel anytime</p>
          </>
        )}
      </main>
    </div>
  );
}
